import {
  SelectEditorDescription_Type,
  type InputConfig,
  type IntegrationDescription,
  type InputDescription,
} from "@smartsuite/automation-engine-api";
import { toJS } from "mobx";
import type React from "react";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../types/candidate";
import { EmailEditor } from "../../EmailEditor";
import { MemberEditor } from "../../MemberEditor";
import { lazy, Suspense, useEffect } from "react";
import { getInAppMessageForTrigger } from "../../utils";
import { useRootStore } from "../../../../../../stores/hooks";
import { isMsTeamIntegration } from "../../../../../../utils/constants";
import { mergeActionName } from "../../../../../../stores/automation/mergers/actions";
import {
  getActionNameInputId,
  pickValueConfigString,
} from "../../../../../../utils/inputs";
import { InputMissingEditor } from "../../InputMissingEditor";
import { SelectEditor as FieldSelectEditor } from "./fields/SelectEditor";
import { SelectEditor } from "../../SelectEditor";
import { StyledContainer, StyledReactInputFieldContainer } from "./style";
import { TestTriggerEditor } from "./fields/TestTriggerEditor";
import { UrlReadOnlyEditor } from "./fields/UrlReadOnlyEditor";
import { JsonEditorWrapper } from "./fields/JsonEditor/JsonEditorWrapper";
import { KeyValueEditor } from "./fields/KeyValueEditor";
import { ReadOnlyEditor } from "./fields/ReadOnlyEditor";
import { CheckboxEditor } from "./fields/CheckboxEditor";
import { LimitSortEditor } from "./fields/LimitSortEditor";
import { ChecklistEditor } from "./fields/ChecklistEditor";
import { FileEditor } from "./fields/FileEditor";
import { SmartDocEditor } from "./fields/SmartDocEditor";
import { FieldsEditor } from "./fields/FieldsEditor";
import { DateEditor } from "./fields/DateEditor";
import { LinkEditor } from "./fields/LinkEditor";
import { PhoneEditor } from "./fields/PhoneEditor";
import { DurationEditor } from "./fields/DurationEditor";
import { NumberEditor } from "./fields/NumberEditor";
import { StringEditor } from "./fields/StringEditor";

// Lazy-loaded so the CodeMirror bundle is split into its own async chunk and
// only fetched when a "code" input is actually rendered.
const CodeEditor = lazy(async () => ({
  default: (await import("./fields/CodeEditor")).CodeEditor,
}));

export const FieldEditorRow: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action: ActionCandidate | undefined;
  integrations: IntegrationDescription[];
  onClickTrash?: (input: InputConfig) => void;
  onRemoveFieldInput?: (input: InputConfig) => void;
  showValidationError: boolean;
  inputDescription?: InputDescription;
}> = ({
  input,
  onChange,
  trigger,
  actions,
  action,
  integrations,
  onClickTrash,
  onRemoveFieldInput,
  showValidationError,
  inputDescription,
}) => {
  const store = useRootStore();
  const actionGroupType =
    store.automations.candidate.currentActionGroup?.config.oneofKind ??
    "actions";

  useEffect(() => {
    const description = action?.description.inputs.find(
      (d) => d.inputId === input.inputId,
    );
    if (description === undefined) {
      return;
    }
    // It looks bad, but we have to do it this way
    if (description.inputId === "in-app-message-input") {
      const { candidate } = store.automations;
      if (candidate.isNew()) {
        const defaultValue = getInAppMessageForTrigger(trigger);
        input.label = description.label;
        input.value = defaultValue;
        onChange(input);
      }
    }
  }, [trigger]);

  const setActionTitle = (input: InputCandidate): void => {
    if (
      action === undefined ||
      description?.inputId !==
        getActionNameInputId(action.actionReference?.actionId) ||
      input.value === undefined
    ) {
      return;
    }
    const actionName = pickValueConfigString(input.value);
    if (actionName === undefined) {
      return;
    }
    store.automations.candidate.setActions(
      [mergeActionName(action?.hash, actionName)],
      actionGroupType,
    );
  };

  const onChangeText = (input: InputCandidate): void => {
    onChange(input);
    setActionTitle(input);
  };
  const description =
    action?.description.inputs.find((d) => d.inputId === input.inputId) ??
    inputDescription;

  const trashAction = (): void => {
    onClickTrash?.(input);
  };

  const needShowTeamInEmail = !isMsTeamIntegration(
    action?.actionReference?.integrationId,
  );

  return description ? (
    <StyledContainer>
      <StyledReactInputFieldContainer>
        {(description.editor?.editor.oneofKind === "select" ||
          description.editor?.editor.oneofKind === "status" ||
          description.editor?.editor.oneofKind === "tag") &&
          (description.editor?.editor.oneofKind === "select" &&
          description.editor?.editor.select.type ===
            SelectEditorDescription_Type.WHITE_TOGGLE ? (
            <SelectEditor
              config={toJS({
                ...input,
                label: description.label,
              })}
              onChange={onChange}
              trigger={trigger}
              description={description}
            />
          ) : (
            <FieldSelectEditor
              input={toJS({
                ...input,
                label: description.label,
              })}
              onChange={onChange}
              trigger={trigger}
              actions={actions}
              action={action}
              integrations={integrations}
              description={description}
              isMultiple={Boolean(input.valueDescription?.multiple)}
              onClickTrash={trashAction}
              showValidationError={showValidationError}
              type={
                description?.editor?.editor.oneofKind === "select"
                  ? description.editor.editor.select.type
                  : undefined
              }
            />
          ))}

        {description.editor?.editor.oneofKind === "text" && (
          <StringEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChangeText}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            description={description}
            isMultiple={description.editor.editor.text.multiple ?? false}
            showValidationError={showValidationError}
            onClickTrash={trashAction}
            disableReferences={
              description.editor.editor.text.disablePills === true
            }
          />
        )}

        {description.editor?.editor.oneofKind === "code" && (
          <Suspense fallback={null}>
            <CodeEditor
              input={toJS({
                ...input,
                label: description.label,
              })}
              onChange={onChange}
              description={description}
              showValidationError={showValidationError}
              onClickTrash={trashAction}
            />
          </Suspense>
        )}

        {description.editor?.editor.oneofKind === "number" && (
          <NumberEditor
            config={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            actions={actions}
            action={action}
            description={description}
            integrations={integrations}
            allowNegative={description.editor.editor.number.allowNegative}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "duration" && (
          <DurationEditor
            config={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            actions={actions}
            action={action}
            description={description}
            integrations={integrations}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "phone" && (
          <PhoneEditor
            description={description}
            editor={description.editor}
            config={{
              ...input,
              label: description.label,
            }}
            integrations={integrations}
            trigger={trigger}
            onChange={onChange}
            actions={actions}
            action={action}
            isMultiple={
              description.editor.editor?.oneofKind === "phone"
                ? description.editor.editor.phone.multiple
                : input.valueDescription?.multiple !== undefined
                  ? input.valueDescription?.multiple
                  : true
            }
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "link" && (
          <LinkEditor
            config={toJS({
              ...input,
              label: description.label,
            })}
            editor={description.editor}
            onChange={onChange}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            isMultiple={description.editor.editor.link.multiple}
            onClickTrash={trashAction}
            description={description}
          />
        )}
        {description.editor?.editor.oneofKind === "email" && (
          <EmailEditor
            description={description}
            editor={description.editor}
            config={{
              ...input,
              label: description.label,
            }}
            trigger={trigger}
            onChange={onChange}
            showValidationError={Boolean(showValidationError)}
            actions={actions}
            action={action}
            integrations={integrations}
            isMultiple={
              description.editor.editor?.oneofKind === "email"
                ? description.editor.editor.email.multiple
                : input.valueDescription?.multiple !== undefined
                  ? input.valueDescription?.multiple
                  : true
            }
            onClickTrash={trashAction}
            showTeams={needShowTeamInEmail}
          />
        )}
        {description.editor?.editor.oneofKind === "member" && (
          <MemberEditor
            description={description}
            config={{
              ...input,
              label: description.label,
            }}
            trigger={trigger}
            onChange={onChange}
            showValidationError
            actions={actions}
            action={action}
            integrations={integrations}
            isMultiple={Boolean(
              description.editor.editor?.oneofKind === "member"
                ? description.editor.editor.member.multiple
                : input.valueDescription?.multiple,
            )}
            onClickTrash={trashAction}
            filterTriggerFields={(option) => {
              if (option.outputId.includes("user-id")) {
                return false;
              }
              return true;
            }}
            filterActionFields={(option) => {
              option.options = option.options.filter((option) => {
                if (option.outputId.includes("user-id")) {
                  return false;
                }
                return true;
              });
              return true;
            }}
          />
        )}

        {description.editor?.editor.oneofKind === "date" && (
          <DateEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            description={description}
            onClickTrash={trashAction}
            useDateWithTime={true}
          />
        )}
        {description.editor?.editor.oneofKind === "outputs" && (
          <FieldsEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            description={description}
            integrations={integrations}
            onClickTrash={trashAction}
          />
        )}

        {description.editor?.editor.oneofKind === "smartdoc" && (
          <SmartDocEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            description={description}
            showValidationError={showValidationError}
            onClickTrash={trashAction}
            type={description.editor.editor.smartdoc.type}
          />
        )}

        {description.editor?.editor.oneofKind === "file" && (
          <FileEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChange}
            trigger={trigger}
            action={action}
            actions={actions}
            description={description}
            integrations={integrations}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "checklist" && (
          <ChecklistEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            description={description}
            integrations={integrations}
            onClickTrash={trashAction}
            trigger={trigger}
            actions={actions}
            action={action}
            showValidationError={showValidationError}
          />
        )}
        {description.editor?.editor.oneofKind === "limitAndSort" && (
          <LimitSortEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            action={action}
            limitAndSortData={description.editor.editor.limitAndSort}
          />
        )}
        {description.editor?.editor.oneofKind === "checkbox" && (
          <CheckboxEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            description={description}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "readOnly" && (
          <ReadOnlyEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            integrations={integrations}
            action={action}
            description={description}
            onClickTrash={trashAction}
            showValidationError={showValidationError}
          />
        )}
        {description.editor?.editor.oneofKind === "keyValue" && (
          <KeyValueEditor
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChangeText}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            description={description}
            showValidationError={showValidationError}
            onClickTrash={trashAction}
          />
        )}
        {(description.editor?.editor.oneofKind === "json" ||
          description.editor?.editor.oneofKind === "jsonStructure") && (
          <JsonEditorWrapper
            input={toJS({
              ...input,
              label: description.label,
            })}
            onChange={onChangeText}
            description={description}
            showValidationError={true}
            onClickTrash={trashAction}
            action={action}
            actions={actions}
            integrations={integrations}
            trigger={trigger}
          />
        )}
        {description.editor?.editor.oneofKind === "urlReadOnly" && (
          <UrlReadOnlyEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            description={description}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "testTrigger" && (
          <TestTriggerEditor
            input={{
              ...input,
              label: description.label,
            }}
            onChange={onChange}
            integrations={integrations}
            trigger={trigger}
            description={description}
            onClickTrash={trashAction}
          />
        )}
        {description.editor?.editor.oneofKind === "hidden" && null}
      </StyledReactInputFieldContainer>
    </StyledContainer>
  ) : input.inputId === "model" ? null : ( // action descriptions no longer list it. Hide "unknown field" instead of prompting removal. // AI Assist backward compatibility: older automations may store a `model` input while dynamic
    <InputMissingEditor
      input={input}
      onClickTrash={onRemoveFieldInput}
      actionHash={action?.hash ?? ""}
    />
  );
};
